import * as React from 'react';
import { Inbox, Plus, Upload } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { StatusChip } from '@/components/ui/status-chip';
import {
  CodeBadge,
  DetailEmpty,
  ListCount,
  ListDetailLayout,
  ListGroupLabel,
  ListItem,
  ListPane,
  SegmentedControl,
  WorkspaceButton,
  linkishClass,
} from '@/components/ui/workspace';
import { useDiscoverySummaryQuery } from '@/lib/api/discovery';
import { useInterviewProposalsQuery } from '@/lib/api/discovery-interview-proposals';
import { useReviewQueueQuery } from '@/lib/api/discovery-review';
import {
  useInterviewsQuery,
  usePeopleQuery,
  type InterviewList,
  type InterviewListItem,
  type PersonListItem,
} from '@/lib/api/discovery-sessions';
import { PersonAvatar } from '../coverage-bits';
import { plural } from '../discovery-logic';
import { useDiscoveryNavigate } from '../discovery-layout';
import { PersonDetailView } from '../people/person-detail';
import { ReviewQueue } from '../transcript-review/review-queue';
import { parseScope, scopeParam } from '../transcript-review/review-logic';
import { AddInterview } from './add-interview';
import { BringInFiles } from './intake';
import { InterviewDetailView } from './interview-detail';
import { RoundDetail } from './round-detail';
import {
  byWhatBlocks,
  listMeta,
  listTitle,
  momentChip,
  momentOf,
  nameOfInterview,
  type Moment,
} from './sessions-logic';
import { PlacementDetail } from './transcripts-to-place';

/**
 * Discovery -> Sessions (T-0186; simplified 23 Oct 2026, design note
 * section 3). The list on the left leads with the two pieces of work,
 * "Review what was said" (the review queue across interviews) and "Bring in
 * files" (bulk intake), then the interviews by moment: after the session
 * (what needs you, what blocks first), coming up, reviewed, and the plan
 * rows nobody is booked for, collapsed behind their counts. Each row leads
 * with the person; the code comes second (rule 20). People and the
 * department round are as before.
 *
 * The address says what is open: `?review=<all|interview id|doc:id|area:id>`,
 * `?intake=1`, `?interview=<id>` (or `new`), `?round=<id>`, `?person=<id>`,
 * `?placement=<id>` (with `change`, the add form pre-filled), `?list=people`.
 * With nothing named, the review queue opens when anything waits, else the
 * first interview that needs you.
 */

export interface SessionsSearch {
  interview?: string;
  person?: string;
  round?: string;
  list?: 'people';
  placement?: string;
  change?: boolean;
  review?: string;
  intake?: boolean;
}

export function DiscoverySessions({
  engagementId,
  search,
  onSearch,
}: {
  engagementId: string;
  search: SessionsSearch;
  onSearch: (next: SessionsSearch) => void;
}) {
  const interviews = useInterviewsQuery(engagementId);
  const people = usePeopleQuery(engagementId);
  const summary = useDiscoverySummaryQuery(engagementId);
  const proposals = useInterviewProposalsQuery(engagementId);
  const queue = useReviewQueueQuery(engagementId);
  const go = useDiscoveryNavigate(engagementId);
  const [showRetired, setShowRetired] = React.useState(false);

  const peopleMode = search.list === 'people' || !!search.person;
  const items = React.useMemo(
    () => interviews.data?.groups.flatMap((g) => g.items) ?? [],
    [interviews.data],
  );
  const nameOf = React.useMemo(() => nameOfInterview(items), [items]);
  const toPlace = proposals.data?.items ?? [];
  const active = (people.data?.items ?? []).filter((p) => p.status !== 'retired');
  const adding = !peopleMode && search.interview === 'new';
  const roundId = peopleMode ? undefined : search.round;
  const placementId = peopleMode ? null : (search.placement ?? null);
  const placement = placementId
    ? (toPlace.find((p) => p.proposal_id === placementId) ?? null)
    : null;
  const changing = !!placement && search.change === true;
  const anythingWaits = (queue.data?.items.length ?? 0) > 0;
  const named =
    peopleMode ||
    adding ||
    !!roundId ||
    !!placementId ||
    !!search.interview ||
    !!search.review ||
    !!search.intake;
  const intakeOpen = !peopleMode && !!search.intake;
  const reviewOpen =
    !peopleMode && (!!search.review || (!named && queue.isSuccess && anythingWaits));
  const interviewId =
    peopleMode || adding || roundId || placementId || reviewOpen || intakeOpen
      ? null
      : (search.interview ?? (items.find((i) => i.needs_you) ?? items[0])?.id ?? null);
  const personId = peopleMode ? (search.person ?? active[0]?.id ?? null) : null;
  const canExtend = summary.data?.permissions.can_move_version ?? false;
  const scope = parseScope(search.review);

  const openInterview = (id: string) => onSearch({ interview: id });
  const openPerson = (id: string) => onSearch({ person: id });

  const head = (
    <>
      <SegmentedControl
        aria-label="Show interviews or people"
        className="flex [&>button]:flex-1"
        value={peopleMode ? 'people' : 'interviews'}
        onChange={(v) => onSearch(v === 'people' ? { list: 'people' } : {})}
        options={[
          { value: 'interviews', label: <Count label="Interviews" n={items.length} /> },
          { value: 'people', label: <Count label="People" n={active.length} /> },
        ]}
      />
      {peopleMode ? null : (
        <WorkspaceButton
          variant="secondary"
          size="md"
          className="w-full justify-center"
          onClick={() => onSearch({ interview: 'new' })}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add interview
        </WorkspaceButton>
      )}
    </>
  );

  const loading = (peopleMode ? people : interviews).isLoading;
  const error = (peopleMode ? people : interviews).error;
  const ready = queue.data?.counts.ready ?? 0;
  const list = (
    <ListPane label={peopleMode ? 'People' : 'Interviews'} head={head}>
      {error ? (
        <div className="p-2">
          <ErrorMessage error={error} />
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 p-3 text-meta text-ink-500">
          <Spinner /> Loading…
        </div>
      ) : peopleMode ? (
        <PeopleList
          people={people.data?.items ?? []}
          selected={personId}
          showRetired={showRetired}
          onToggleRetired={() => setShowRetired((v) => !v)}
          onPick={openPerson}
        />
      ) : (
        <>
          <ListItem
            selected={reviewOpen}
            onClick={() => onSearch({ review: 'all' })}
            leading={<Inbox className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />}
            title="Review what was said"
            meta={
              ready > 0
                ? `${plural(ready, 'item')} from ${plural(
                    (queue.data?.counts.interviews ?? 0) + (queue.data?.counts.documents ?? 0),
                    'source',
                  )}`
                : 'Nothing waiting'
            }
            trailing={ready > 0 ? <ListCount title="Waiting for review">{ready}</ListCount> : null}
          />
          <ListItem
            selected={intakeOpen || !!placementId}
            onClick={() => onSearch({ intake: true })}
            leading={<Upload className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />}
            title="Bring in files"
            meta={
              toPlace.length > 0
                ? `${plural(toPlace.length, 'transcript')} to place`
                : 'Transcripts and documents, many at once'
            }
            trailing={
              toPlace.length > 0 ? (
                <StatusChip tone="amber" size="sm">
                  {toPlace.length}
                </StatusChip>
              ) : null
            }
          />
          <InterviewsByMoment
            data={interviews.data}
            adding={adding}
            selected={roundId ?? interviewId}
            onPick={openInterview}
            onPickRound={(id) => onSearch({ round: id })}
          />
        </>
      )}
    </ListPane>
  );

  let detail: React.ReactNode;
  if (peopleMode) {
    detail = personId ? (
      <PersonDetailView
        key={personId}
        engagementId={engagementId}
        personId={personId}
        onOpenInterview={openInterview}
        onOpenPerson={openPerson}
      />
    ) : (
      <DetailEmpty title="Pick a person" text="Their interviews, answers and evidence open here." />
    );
  } else if (adding) {
    detail = (
      <AddInterview
        engagementId={engagementId}
        nextCode={interviews.data ? nextCode(items) : null}
        onCancel={() => onSearch({})}
        onAdded={openInterview}
      />
    );
  } else if (placement && changing) {
    detail = (
      <AddInterview
        key={`change-${placement.proposal_id}`}
        engagementId={engagementId}
        nextCode={interviews.data ? nextCode(items) : null}
        placement={placement}
        onCancel={() => onSearch({ intake: true })}
        onAdded={openInterview}
      />
    );
  } else if (placement) {
    detail = (
      <PlacementDetail
        key={placement.proposal_id}
        engagementId={engagementId}
        proposal={placement}
        onConfirmed={openInterview}
        onChange={() => onSearch({ placement: placement.proposal_id, change: true })}
        onDeclined={() => onSearch({ intake: true })}
      />
    );
  } else if (placementId && !proposals.isLoading) {
    detail = (
      <DetailEmpty
        title="That placement is settled"
        text="It was confirmed or declined. Bring in files lists what is still to place."
      />
    );
  } else if (intakeOpen) {
    detail = (
      <BringInFiles
        engagementId={engagementId}
        interviews={items}
        onReview={(documentId) => onSearch({ review: `doc:${documentId}` })}
        onChange={(proposalId) => onSearch({ placement: proposalId, change: true })}
        onOpenInterview={openInterview}
      />
    );
  } else if (reviewOpen) {
    detail = (
      <ReviewQueue
        engagementId={engagementId}
        scope={scope}
        onScope={(s) => onSearch({ review: scopeParam(s) })}
        onOpenInterview={openInterview}
        interviewName={nameOf}
      />
    );
  } else if (roundId) {
    detail = (
      <RoundDetail
        key={roundId}
        engagementId={engagementId}
        roundId={roundId}
        canExtend={canExtend}
      />
    );
  } else if (interviewId) {
    detail = (
      <InterviewDetailView
        key={interviewId}
        engagementId={engagementId}
        interviewId={interviewId}
        canExtend={canExtend}
        onOpenPerson={openPerson}
        onOpenArea={(area) => go({ to: 'findings', area })}
        interviewName={nameOf}
      />
    );
  } else if (!interviews.isLoading) {
    detail = <DetailEmpty title="Pick an interview" text="Or add one." />;
  }

  return (
    <ListDetailLayout
      list={list}
      detailKey={
        personId ??
        (adding
          ? 'new'
          : placementId
            ? `${placementId}${changing ? ':change' : ''}`
            : intakeOpen
              ? 'intake'
              : reviewOpen
                ? `review:${scopeParam(scope)}`
                : (roundId ?? interviewId ?? ''))
      }
    >
      {detail}
    </ListDetailLayout>
  );
}

function Count({ label, n }: { label: string; n: number }) {
  return (
    <>
      {label} <span className="ml-0.5 font-mono text-[11px] font-normal opacity-75">{n}</span>
    </>
  );
}

/** The code the next interview gets; the API allocates it (rule 20), this only names it. */
function nextCode(items: ReadonlyArray<{ code: string }>): string {
  const n = items.reduce((m, i) => Math.max(m, Number(i.code.slice(1)) || 0), 0);
  return `S${n + 1}`;
}

const MOMENTS: ReadonlyArray<{ key: Moment; label: string; collapsed: boolean; note?: string }> = [
  { key: 'after', label: 'After the session', collapsed: false },
  { key: 'coming', label: 'Coming up', collapsed: false },
  { key: 'reviewed', label: 'Reviewed', collapsed: true },
  {
    key: 'unplanned',
    label: 'Not planned yet',
    collapsed: true,
    note: 'Plan rows from the starting plan with no one named or scheduled.',
  },
  { key: 'cancelled', label: 'Cancelled', collapsed: true },
];

function InterviewsByMoment({
  data,
  adding,
  selected,
  onPick,
  onPickRound,
}: {
  data: InterviewList | undefined;
  adding: boolean;
  selected: string | null;
  onPick: (id: string) => void;
  onPickRound: (id: string) => void;
}) {
  const [shown, setShown] = React.useState<Set<Moment>>(new Set());
  const all = (data?.groups ?? []).flatMap((g) => g.items);
  const round = data?.department_round;
  const byMoment = new Map<Moment, InterviewListItem[]>();
  for (const i of all) {
    const m = momentOf(i);
    byMoment.set(m, [...(byMoment.get(m) ?? []), i]);
  }
  byMoment.get('after')?.sort(byWhatBlocks);
  const item = (i: InterviewListItem) => {
    const chip = momentChip(i.state, i.scheduled_at, i.people.length > 0);
    return (
      <ListItem
        key={i.id}
        selected={!adding && selected === i.id}
        onClick={() => onPick(i.id)}
        leading={<Lead i={i} />}
        title={listTitle(i)}
        meta={listMeta(i)}
        trailing={
          <StatusChip tone={chip.tone} size="sm">
            {chip.label}
          </StatusChip>
        }
      />
    );
  };
  return (
    <>
      {adding ? (
        <ListItem
          selected
          leading={<Plus className="h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />}
          title="New interview"
        />
      ) : null}
      {all.length === 0 && !round ? (
        <div className="px-2 py-3 text-meta text-ink-500">No interviews yet. Add one.</div>
      ) : null}
      {MOMENTS.map((m) => {
        const list = byMoment.get(m.key) ?? [];
        if (list.length === 0 && (m.key === 'cancelled' || m.key === 'reviewed')) return null;
        if (list.length === 0 && all.length === 0) return null;
        const open =
          !m.collapsed || shown.has(m.key) || list.some((i) => i.id === selected && !adding);
        return (
          <div key={m.key}>
            <ListGroupLabel
              count={list.length}
              action={
                m.collapsed && list.length > 0 ? (
                  <button
                    type="button"
                    className={linkishClass()}
                    onClick={() =>
                      setShown((cur) => {
                        const next = new Set(cur);
                        if (next.has(m.key)) next.delete(m.key);
                        else next.add(m.key);
                        return next;
                      })
                    }
                  >
                    {open ? 'Hide' : 'Show'}
                  </button>
                ) : undefined
              }
            >
              {m.label}
            </ListGroupLabel>
            {open ? list.map(item) : null}
            {!open && m.note ? <div className="px-2 text-meta text-ink-500">{m.note}</div> : null}
          </div>
        );
      })}
      {round ? (
        <>
          <ListGroupLabel>Department round</ListGroupLabel>
          <ListItem
            selected={!adding && selected === round.round_id}
            onClick={() => onPickRound(round.round_id)}
            leading={<CodeBadge>{round.questionnaire.code}</CodeBadge>}
            title={round.questionnaire.name}
            meta={`${round.answered} of ${round.departments} departments answered`}
          />
        </>
      ) : null}
    </>
  );
}

/** The person first: an avatar, two stacked for a group, a dashed "?" for no one named. */
function Lead({ i }: { i: InterviewListItem }) {
  if (i.people.length === 1) {
    return (
      <span aria-hidden="true" className="flex">
        <PersonAvatar id={i.people[0]!.id} name={i.people[0]!.full_name} size={26} />
      </span>
    );
  }
  if (i.people.length > 1) {
    return (
      <span aria-hidden="true" className="inline-flex flex-none">
        {i.people.slice(0, 2).map((p, n) => (
          <span key={p.id} className={n > 0 ? '-ml-1.5' : undefined}>
            <PersonAvatar id={p.id} name={p.full_name} size={22} />
          </span>
        ))}
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className="inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-full border border-dashed border-ink-300 text-[11px] text-ink-300"
    >
      ?
    </span>
  );
}

function PeopleList({
  people,
  selected,
  showRetired,
  onToggleRetired,
  onPick,
}: {
  people: ReadonlyArray<PersonListItem>;
  selected: string | null;
  showRetired: boolean;
  onToggleRetired: () => void;
  onPick: (id: string) => void;
}) {
  const groups: Array<{ name: string; items: PersonListItem[] }> = [];
  const retired: PersonListItem[] = [];
  for (const p of people) {
    if (p.status === 'retired') {
      retired.push(p);
      continue;
    }
    const name = p.department?.trim() || 'No department';
    let g = groups.find((x) => x.name === name);
    if (!g) groups.push((g = { name, items: [] }));
    g.items.push(p);
  }
  const item = (p: PersonListItem) => (
    <ListItem
      key={p.id}
      selected={selected === p.id}
      onClick={() => onPick(p.id)}
      leading={
        <span aria-hidden="true" className="flex">
          <PersonAvatar id={p.id} name={p.full_name} size={24} />
        </span>
      }
      title={p.full_name}
      meta={p.title || 'No title yet'}
      trailing={
        p.status === 'retired' ? (
          <StatusChip tone="frozen" size="sm">
            Retired
          </StatusChip>
        ) : p.possible_duplicate_of ? (
          <StatusChip tone="at-risk" size="sm">
            Possible duplicate
          </StatusChip>
        ) : null
      }
    />
  );
  return (
    <>
      {groups.length === 0 && retired.length === 0 ? (
        <div className="px-2 py-3 text-meta text-ink-500">No people yet.</div>
      ) : null}
      {groups.map((g) => (
        <div key={g.name}>
          <ListGroupLabel>{g.name}</ListGroupLabel>
          {g.items.map(item)}
        </div>
      ))}
      {retired.length > 0 ? (
        <>
          <ListGroupLabel
            count={retired.length}
            action={
              <button type="button" className={linkishClass()} onClick={onToggleRetired}>
                {showRetired ? 'Hide' : 'Show'}
              </button>
            }
          >
            Retired
          </ListGroupLabel>
          {showRetired || retired.some((p) => p.id === selected) ? retired.map(item) : null}
        </>
      ) : null}
    </>
  );
}
